// src/modules/operaciones/inventarios/components/KpisInventario.js
//
// Tarjetas de KPIs, Sparkline, Stepper de flujo y skeleton de tarjetas.
import React from "react"; // Importa React (React.Fragment en el Stepper)
import { fmt } from "../../../../utils/format"; // Formato de números compartido

// Mini gráfico de línea SVG para las tarjetas KPI
const Sparkline = ({ datos = [], color = "#007aff", height = 32 }) => {
  const puntos = datos.length > 0 ? datos : [0, 0, 0, 0, 0, 0, 0]; // Usa ceros si no hay datos
  const max = Math.max(...puntos, 1); // Valor máximo (mínimo 1)
  const min = Math.min(...puntos, 0); // Valor mínimo (máximo 0)
  const rango = max - min || 1; // Rango evitando división por cero
  const ancho = 100; // Ancho del viewBox
  const paso = ancho / Math.max(puntos.length - 1, 1); // Separación horizontal entre puntos
  const coords = puntos.map((v, i) => `${(i * paso).toFixed(2)},${(height - ((v - min) / rango) * (height - 4) - 2).toFixed(2)}`); // Convierte datos a coordenadas x,y
  const path = `M ${coords.join(" L ")}`; // Construye el trazo de la línea
  return (
    // SVG responsive que se estira al ancho disponible
    <svg viewBox={`0 0 ${ancho} ${height}`} className="w-full h-8" preserveAspectRatio="none" aria-hidden="true">
      {/* Área rellena bajo la línea con baja opacidad */}
      <path d={`${path} L ${ancho},${height} L 0,${height} Z`} fill={color} opacity="0.15" />
      {/* Línea del gráfico */}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// Tarjeta de indicador con valor y mini gráfico
const KpiCard = ({ label, valor, color, spark, sparkColor, icono, tema }) => (
  // Contenedor de la tarjeta con efecto hover
  <div
    className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-3xl border shadow-sm p-5 flex flex-col gap-1 transition-all hover:shadow-md hover:-translate-y-0.5`}
  >
    {/* Fila superior: título e icono */}
    <div className="flex items-center justify-between">
      <p className={`text-[10px] font-bold uppercase tracking-wide ${tema.textoSuave}`}>{label}</p>
      {icono && <span className="text-base opacity-70">{icono}</span>}
    </div>
    {/* Valor numérico formateado */}
    <p className={`text-2xl font-extrabold ${color}`}>{fmt(valor, 3)}</p>
    {/* Mini gráfico si hay datos */}
    {spark && <Sparkline datos={spark} color={sparkColor || "#007aff"} />}
  </div>
);

// Indicador de pasos del flujo: Cargar → Recalcular → Exportar
export const Stepper = ({ pendientes, hayPrecios, totalAjustes, tema }) => {
  // Define los 3 pasos y su estado (done/activo)
  const pasos = [
    { id: 1, label: "Cargar", icono: "📥", done: totalAjustes > 0 }, // Completo si hay ajustes cargados
    { id: 2, label: "Recalcular", icono: "🧮", done: hayPrecios && pendientes === 0, activo: hayPrecios && pendientes > 0 }, // Activo si faltan precios
    { id: 3, label: "Exportar", icono: "📊", done: false, activo: hayPrecios && pendientes === 0 }, // Activo cuando no hay pendientes
  ];
  return (
    // Contenedor con scroll horizontal en pantallas pequeñas
    <div
      className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-3xl border shadow-sm p-4 mb-6 flex items-center justify-between gap-2 overflow-x-auto`}
    >
      {/* Recorre los pasos; Fragment permite agrupar paso y conector */}
      {pasos.map((p, i) => (
        <React.Fragment key={p.id}>
          {/* Bloque del paso: círculo y texto */}
          <div className="flex items-center gap-2.5 shrink-0">
            {/* Círculo: verde si está hecho, ámbar pulsante si está activo, neutro si no */}
            <div
              className={`w-9 h-9 rounded-2xl flex items-center justify-center text-sm font-bold border transition-all ${
                p.done
                  ? "bg-emerald-500 text-white border-emerald-500 shadow-md"
                  : p.activo
                  ? "bg-amber-500 text-white border-amber-500 shadow-md animate-pulse"
                  : `${tema.inputBg} ${tema.textoMuted} ${tema.inputBorde}`
              }`}
            >
              {p.done ? "✓" : p.icono}
            </div>
            {/* Texto del paso, oculto en móvil */}
            <div className="hidden sm:block">
              <p className={`text-[11px] font-bold ${p.done || p.activo ? tema.textoFuerte : tema.textoMuted}`}>{p.label}</p>
              {p.id === 2 && pendientes > 0 && <p className="text-[10px] text-amber-600 font-bold">{fmt(pendientes, 3)} pendientes</p>}
              {p.id === 2 && hayPrecios && pendientes === 0 && <p className="text-[10px] text-emerald-600 font-bold">Completo</p>}
            </div>
          </div>
          {/* Línea conectora entre pasos (no tras el último) */}
          {i < pasos.length - 1 && <div className={`flex-1 h-0.5 min-w-[20px] rounded ${p.done ? "bg-emerald-500" : tema.inputBg}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// Placeholder animado de las tarjetas KPI mientras carga
export const SkeletonCards = ({ tema }) => (
  // Grilla de 4 tarjetas fantasma
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    {/* Genera 4 tarjetas de carga */}
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-3xl border shadow-sm p-5 animate-pulse`}>
        {/* Barras grises que simulan etiqueta y valor */}
        <div className="h-2 bg-gray-300/50 rounded w-16 mb-3" />
        <div className="h-6 bg-gray-300/50 rounded w-20 mb-3" />
        <div className="h-6 bg-gray-300/40 rounded w-full" />
      </div>
    ))}
  </div>
);

// Fila de tarjetas KPI (extraída del render de Invent.js sin cambios de markup)
export default function KpisInventario({ tarjetasKpi, sparkVolumen, tema }) {
  return (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
    {tarjetasKpi.map((k) => (
      <KpiCard key={k.label} {...k} spark={sparkVolumen} tema={tema} />
    ))}
  </div>
  );
}